import type React from 'react';
import { useEffect, useId, useState } from 'react';
import Alert, { type AlertType } from './Alert';
import Button from './Button';
import CheckCircleFilled from './CustomIcons/CheckCircleFilled';
import CheckIcon from './CustomIcons/CheckIcon';
import CopyIcon from './CustomIcons/CopyIcon';
import ErrorCircleFilled from './CustomIcons/ErrorCircleFilled';
import InfoCircleFilled from './CustomIcons/InfoCircleFilled';
import WarningCircleFilled from './CustomIcons/WarningCircleFilled';
import FieldMessage from './FieldMessage';
import { Input } from './Input';
import Modal, { type ModalSize } from './Modal';
import Typography from './Typography';
import {
    CONFIRMATION_DIALOG_VARIANTS,
    type ConfirmationDialogVariant,
} from './confirmationDialogVariants';

/** Kept beside the component: an icon is a node, not a value the variant table can hold. */
const BADGE_GLYPH_SIZE = 12;

const COPIED_FEEDBACK_MS = 1200;

const collapseSpaces = (value: string) => value.replace(/\s+/g, ' ');

const nearMissAgainst = (typedPhrase: string, confirmPhrase?: string): string | undefined => {
    const typed = typedPhrase.trim();
    if (!confirmPhrase || typed === '' || typed === confirmPhrase) {
        return undefined;
    }
    if (collapseSpaces(typed).toLowerCase() !== collapseSpaces(confirmPhrase).toLowerCase()) {
        return undefined;
    }
    return collapseSpaces(typed) === collapseSpaces(confirmPhrase)
        ? 'The spacing has to match exactly.'
        : 'This is case-sensitive.';
};

type PhraseLabel = string | { prefix: string; suffix?: string };

type CopyOutcome = 'idle' | 'copied' | 'failed';

const COPY_FAILED_MESSAGE = 'Could not copy. Enter the phrase manually.';

const resolvePhraseLabel = (
    label: PhraseLabel | undefined,
    phrase?: string,
): { parts?: { prefix: string; suffix?: string }; text: string } => {
    const parts = typeof label === 'object' && label !== null ? label : undefined;
    if (parts) {
        return { parts, text: [parts.prefix, phrase, parts.suffix].filter(Boolean).join(' ') };
    }
    return { text: typeof label === 'string' ? label : `Type ${phrase} to confirm` };
};

const copyStatusFor = (outcome: CopyOutcome, phrase: string): string =>
    outcome === 'copied' ? `${phrase} copied to clipboard` : '';

const usePhraseCopy = (
    phrase: string | undefined,
    open: boolean,
    disabled: boolean,
    onCopied?: (phrase: string) => void,
): { outcome: CopyOutcome; canCopy: boolean; copy: () => void } => {
    const decisionKey = `${open}|${phrase}`;
    const [state, setState] = useState<{
        key: string;
        generation: number;
        outcome: CopyOutcome;
    }>({ key: decisionKey, generation: 0, outcome: 'idle' });
    if (state.key !== decisionKey) {
        setState((previous) => ({
            key: decisionKey,
            generation: previous.generation + 1,
            outcome: 'idle',
        }));
    }

    useEffect(() => {
        if (state.outcome !== 'copied') {
            return;
        }
        const timer = setTimeout(
            () => setState((previous) => ({ ...previous, outcome: 'idle' })),
            COPIED_FEEDBACK_MS,
        );
        return () => clearTimeout(timer);
    }, [state.outcome]);

    const canCopy = typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText;

    const run = async () => {
        if (!phrase || !canCopy) {
            return;
        }
        const generation = state.generation;
        const settle = (outcome: CopyOutcome) =>
            setState((previous) =>
                previous.generation === generation ? { ...previous, outcome } : previous,
            );
        try {
            await navigator.clipboard.writeText(phrase);
        } catch {
            settle('failed');
            return;
        }
        settle('copied');
        queueMicrotask(() => onCopied?.(phrase));
    };

    return { outcome: state.outcome, canCopy: canCopy && !disabled, copy: () => void run() };
};

const LabelText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography.Text size="xsmall" color="primary" fontWeight="semi-bold">
        {children}
    </Typography.Text>
);

interface PhraseFieldProps {
    phrase: string;
    parts?: { prefix: string; suffix?: string };
    plainLabel: string;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
    copyLabel: string;
    copyOutcome: CopyOutcome;
    canCopy: boolean;
    onCopy: () => void;
    nearMissMessage?: string;
}

const PhraseField: React.FC<PhraseFieldProps> = ({
    phrase,
    parts,
    plainLabel,
    placeholder,
    value,
    onChange,
    disabled,
    copyLabel,
    copyOutcome,
    canCopy,
    onCopy,
    nearMissMessage,
}) => {
    const beforeId = useId();
    const afterId = useId();
    const messageId = useId();
    const message =
        [nearMissMessage, copyOutcome === 'failed' ? COPY_FAILED_MESSAGE : undefined]
            .filter(Boolean)
            .join(' ') || undefined;
    const labelledBy = parts
        ? [beforeId, parts.suffix ? afterId : undefined].filter(Boolean).join(' ')
        : undefined;

    return (
        <div className="flex flex-col gap-[6px]">
            {parts ? (
                <div className="flex flex-wrap items-center gap-[6px] px-2">
                    <span id={beforeId}>
                        <LabelText>{parts.prefix}</LabelText>{' '}
                        <Typography.CodeText variant="inline" size="small">
                            {phrase}
                        </Typography.CodeText>
                    </span>
                    <Button
                        variant="ghost"
                        size="small"
                        iconOnly
                        aria-label={`${copyLabel} ${phrase}`}
                        icon={
                            copyOutcome === 'copied' ? (
                                <CheckIcon size={13} />
                            ) : (
                                <CopyIcon size={13} />
                            )
                        }
                        onClick={onCopy}
                        disabled={!canCopy}
                    />
                    {parts.suffix ? (
                        <span id={afterId}>
                            <LabelText>{parts.suffix}</LabelText>
                        </span>
                    ) : null}
                </div>
            ) : null}
            <Input
                name="confirmation-phrase"
                size="large"
                value={value}
                onChange={onChange}
                label={parts ? undefined : plainLabel}
                aria-labelledby={labelledBy}
                placeholder={placeholder}
                requiredIndicator="none"
                error={!!nearMissMessage}
                aria-describedby={message ? messageId : undefined}
                disabled={disabled}
            />
            <FieldMessage id={messageId} message={message} />
            {parts ? (
                <span role="status" className="sr-only">
                    {copyStatusFor(copyOutcome, phrase)}
                </span>
            ) : null}
        </div>
    );
};

const VARIANT_ICON: Record<ConfirmationDialogVariant, React.ReactNode> = {
    default: <InfoCircleFilled size={BADGE_GLYPH_SIZE} />,
    warning: <WarningCircleFilled size={BADGE_GLYPH_SIZE} />,
    destructive: <ErrorCircleFilled size={BADGE_GLYPH_SIZE} />,
    success: <CheckCircleFilled size={BADGE_GLYPH_SIZE} />,
};

export interface ConfirmationDialogProps {
    open: boolean;
    /** Dismiss handler — cancel, close button, ESC or scrim. `loading` suppresses every
     *  route except ESC, which always reaches this so a keyboard user is never stranded. */
    onClose: () => void;
    /** Runs when the confirm action is taken. Closing is left to the consumer. */
    onConfirm: () => void;
    title: React.ReactNode;
    titleAdornment?: React.ReactNode;
    badgeIcon?: React.ReactNode;
    /** The sentence saying what is about to happen. */
    description?: React.ReactNode;
    variant?: ConfirmationDialogVariant;
    /** Coloured callout below the body. Defaults to the variant's own type. */
    callout?: { type?: AlertType; message: string };
    /**
     * Requires this exact phrase to be typed before the confirm action enables — for a
     * consequence severe enough that a single click should not be able to cause it.
     */
    confirmPhrase?: string;
    /** Label above the type-to-confirm field. `{ prefix, suffix }` makes the phrase copyable. */
    confirmPhraseLabel?: PhraseLabel;
    /** Names the copy control, e.g. `Copy profile name`. Default: `Copy`. */
    confirmPhraseCopyLabel?: string;
    /** Runs after the phrase reaches the clipboard, with what was copied. */
    onPhraseCopied?: (phrase: string) => void;
    confirmPhrasePlaceholder?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /**
     * Disables the actions, spins the confirm button and suppresses the pointer dismissal
     * routes — but never ESC, which always reaches `onClose`.
     */
    loading?: boolean;
    tip?: { label?: string; message: string };
    size?: ModalSize;
    /** Accessible dialog name; required when `title` isn't a plain string. */
    'aria-label'?: string;
    /** ID of a visible element naming the dialog (alternative to `aria-label`). */
    'aria-labelledby'?: string;
    /** Extra body content between the description and the callout, e.g. an entity chip. */
    children?: React.ReactNode;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    open,
    onClose,
    onConfirm,
    title,
    titleAdornment,
    badgeIcon,
    description,
    variant = 'default',
    callout,
    confirmPhrase,
    confirmPhraseLabel,
    confirmPhraseCopyLabel = 'Copy',
    onPhraseCopied,
    confirmPhrasePlaceholder,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    loading = false,
    tip,
    size = 'medium',
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    children,
}) => {
    const [typedPhrase, setTypedPhrase] = useState('');
    const descriptionId = useId();
    const base = CONFIRMATION_DIALOG_VARIANTS[variant];

    // On open, not close: the dialog outlives `open` by its exit transition, so resetting on
    // close empties the field in front of the user. Adjusted during render rather than from an
    // effect, or a reopen paints the previous answer once with confirm already enabled.
    const [answeredFor, setAnsweredFor] = useState({ open, confirmPhrase });
    if (answeredFor.open !== open || answeredFor.confirmPhrase !== confirmPhrase) {
        setAnsweredFor({ open, confirmPhrase });
        if (open) {
            setTypedPhrase('');
        }
    }

    const { parts: phraseParts, text: phraseLabel } = resolvePhraseLabel(
        confirmPhraseLabel,
        confirmPhrase,
    );
    const {
        outcome: copyOutcome,
        canCopy: canCopyPhrase,
        copy: handleCopyPhrase,
    } = usePhraseCopy(confirmPhrase, open, loading, onPhraseCopied);
    // Trimmed because copying an entity name commonly picks up a trailing space; the gate is
    // still exact, and deliberately case-sensitive.
    const phraseSatisfied = !confirmPhrase || typedPhrase.trim() === confirmPhrase;
    const nearMissMessage = nearMissAgainst(typedPhrase, confirmPhrase);
    const confirmDisabled = loading || !phraseSatisfied;

    const footer = (
        <div className="flex w-full flex-wrap items-center justify-end gap-[8px]">
            <Button variant="outline" size="small" onClick={onClose} disabled={loading}>
                {cancelLabel}
            </Button>
            <Button
                variant={base.confirmVariant}
                size="small"
                onClick={onConfirm}
                disabled={confirmDisabled}
                loading={loading}
                // A disabled control emits no hover, so the tooltip explains the gate from the
                // wrapper Button renders for exactly this case.
                tooltip={!loading && !phraseSatisfied ? phraseLabel : undefined}
            >
                {confirmLabel}
            </Button>
        </div>
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={title}
            size={size}
            footer={footer}
            busy={loading}
            // A consequential choice is an alertdialog: it asks assistive tech to announce the
            // body, which is where the consequence is written.
            role={variant === 'destructive' ? 'alertdialog' : 'dialog'}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={description ? descriptionId : undefined}
            // A choice this deliberate should not be undone by a stray click, and a scrim
            // click must not discard a phrase the customer has already typed.
            closeOnOverlayClick={variant !== 'destructive' && !confirmPhrase}
            headerDivider
            titleAdornment={titleAdornment}
            bodyPadding={false}
            tray={{ tone: base.tray, caption: tip }}
            icon={
                <span
                    aria-hidden="true"
                    className="confirmation-badge flex size-[18px] shrink-0 items-center justify-center rounded-[7px] text-[var(--malachite-card-background)]"
                    style={{ backgroundColor: base.badge }}
                >
                    {badgeIcon ?? VARIANT_ICON[variant]}
                </span>
            }
        >
            {callout ? (
                <div className="border-0 border-b border-solid border-[var(--malachite-card-subtle-divider,var(--malachite-card-border))] p-[10px]">
                    <Alert
                        type={callout.type ?? base.calloutType}
                        message={callout.message}
                        hasMargin={false}
                    />
                </div>
            ) : null}
            <div className="flex flex-col gap-[12px] p-[28px]">
                {description ? (
                    <div
                        id={descriptionId}
                        className="text-[13px] leading-[1.6] text-[var(--malachite-text-secondary)]"
                    >
                        {description}
                    </div>
                ) : null}
                {children}
                {confirmPhrase ? (
                    <PhraseField
                        phrase={confirmPhrase}
                        parts={phraseParts}
                        plainLabel={phraseLabel}
                        placeholder={confirmPhrasePlaceholder}
                        value={typedPhrase}
                        onChange={setTypedPhrase}
                        disabled={loading}
                        copyLabel={confirmPhraseCopyLabel}
                        copyOutcome={copyOutcome}
                        canCopy={canCopyPhrase}
                        onCopy={handleCopyPhrase}
                        nearMissMessage={nearMissMessage}
                    />
                ) : null}
            </div>
        </Modal>
    );
};

export default ConfirmationDialog;
export { ConfirmationDialog };
export type { ConfirmationDialogVariant };
